import { z } from 'zod';
import { id, paginationQuery, paginatedResult } from './common.js';
import { shiftSearchQuery } from './shift-search.js';

export const savedSearchCriteria = z
  .object({
    role: shiftSearchQuery.shape.role,
    locationId: shiftSearchQuery.shape.locationId,
    from: shiftSearchQuery.shape.from,
    to: shiftSearchQuery.shape.to,
    eligibleOnly: z.boolean().default(true),
    search: shiftSearchQuery.shape.search,
    minRateCents: shiftSearchQuery.shape.minRateCents,
    radiusMiles: shiftSearchQuery.shape.radiusMiles,
    originPostalCode: shiftSearchQuery.shape.originPostalCode,
    sort: shiftSearchQuery.shape.sort.default('soonest'),
    bookingMode: shiftSearchQuery.shape.bookingMode,
    dateMode: z.enum(['fixed', 'next7', 'next30']).default('fixed'),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.dateMode !== 'fixed' && (value.from || value.to))
      ctx.addIssue({
        code: 'custom',
        path: ['dateMode'],
        message: 'Rolling searches cannot also keep exact dates.',
      });
    const check = shiftSearchQuery.safeParse({
      ...value,
      eligible: value.eligibleOnly ? 'true' : 'false',
    });
    if (!check.success)
      for (const issue of check.error.issues)
        ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
  });
export type SavedSearchCriteria = z.infer<typeof savedSearchCriteria>;
export const savedSearch = z.object({
  id,
  name: z.string(),
  criteria: savedSearchCriteria,
  revision: z.number().int().positive(),
  deleted: z.boolean(),
  summary: z.array(z.string()),
  applyUrl: z.string().startsWith('/find?'),
  staleDates: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedSearch = z.infer<typeof savedSearch>;
export const savedSearchAccess = z.object({ canWrite: z.boolean(), notice: z.string() });
export const savedSearchApplyQuery = z
  .object({ expectedRevision: z.coerce.number().int().positive() })
  .strict();
export const savedSearchPage = paginatedResult(savedSearch).extend({ canWrite: z.boolean() });
export const savedSearchQuery = paginationQuery
  .extend({ search: z.string().trim().max(100).optional() })
  .strict();
const name = z.string().trim().min(1, 'Enter a search name.').max(100);
const request = { clientRequestId: z.string().uuid() };
const target = { id, expectedRevision: z.number().int().positive() };
export const savedSearchMutation = z.discriminatedUnion('action', [
  z
    .object({ ...request, action: z.literal('create'), name, criteria: savedSearchCriteria })
    .strict(),
  z
    .object({
      ...request,
      ...target,
      action: z.literal('update'),
      name,
      criteria: savedSearchCriteria,
    })
    .strict(),
  z.object({ ...request, ...target, action: z.literal('duplicate'), name }).strict(),
  z.object({ ...request, ...target, action: z.literal('delete') }).strict(),
]);
export type SavedSearchMutation = z.infer<typeof savedSearchMutation>;
export type SavedSearchQuery = z.infer<typeof savedSearchQuery>;
